/*
  Instant category switching for the collection toolbar (snippets/brcr-collection-toolbar.liquid).

  Category links stay ordinary links to each collection's own URL, so search engines and
  shoppers who open them in a new tab get the full page. A plain click instead fetches only
  this section for the target collection (Section Rendering API), swaps the heading, toolbar
  and product grid, and pushes the real URL, title, description and canonical. Links are
  prefetched on hover/touch. Anything unexpected falls back to a normal page load.
  Links marked data-brcr-page-load (a collection on a different template, set by the
  toolbar snippet) are never swapped or prefetched: they load as normal pages.

  Filtering and sorting inside a collection are still handled by facets.js; popstate between
  different collections is handled here first (capture phase) so facets.js does not try to
  re-filter the wrong collection.
*/
(() => {
  if (window.brcrCollectionNav) return;
  window.brcrCollectionNav = true;

  const CACHE_MS = 120000;
  const cache = new Map();
  let currentPath = window.location.pathname;
  let navToken = 0;

  const toolbar = () => document.querySelector('[data-brcr-toolbar]');

  function sectionUrl(href) {
    const bar = toolbar();
    if (!bar || !bar.dataset.sectionId) return null;
    const url = new URL(href, window.location.origin);
    url.searchParams.set('section_id', bar.dataset.sectionId);
    return url.toString();
  }

  function fetchSection(href) {
    const key = sectionUrl(href);
    if (!key) return Promise.reject(new Error('no section'));
    const hit = cache.get(key);
    if (hit && Date.now() - hit.time < CACHE_MS) return hit.promise;

    const promise = fetch(key, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // A collection on a different template returns a different (or no) section.
        if (!doc.querySelector('[data-brcr-toolbar]') || !doc.getElementById('ProductGridContainer')) {
          throw new Error('template mismatch');
        }
        return doc;
      });
    promise.catch(() => cache.delete(key));
    cache.set(key, { promise, time: Date.now() });
    return promise;
  }

  function setActive(href) {
    const path = new URL(href, window.location.origin).pathname;
    document.querySelectorAll('[data-brcr-toolbar] .brcr-ctoolbar__cat').forEach((link) => {
      const on = new URL(link.href).pathname === path;
      link.classList.toggle('is-active', on);
      if (on) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  }

  function setHeadTag(selector, attr, value, create) {
    let el = document.head.querySelector(selector);
    if (!value) {
      if (el) el.remove();
      return;
    }
    if (!el) {
      el = create();
      document.head.appendChild(el);
    }
    el.setAttribute(attr, value);
  }

  function announce(text) {
    let region = document.getElementById('BrcrCollectionNavStatus');
    if (!region) {
      region = document.createElement('div');
      region.id = 'BrcrCollectionNavStatus';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }
    region.textContent = text;
  }

  function swap(doc) {
    const newBar = doc.querySelector('[data-brcr-toolbar]');
    const bar = toolbar();
    const hadFocus = bar && bar.contains(document.activeElement);

    const heading = document.querySelector('[data-brcr-heading]');
    const newHeading = doc.querySelector('[data-brcr-heading]');
    if (heading && newHeading) heading.innerHTML = newHeading.innerHTML;

    bar.innerHTML = newBar.innerHTML;
    Object.assign(bar.dataset, newBar.dataset);

    // The grid container itself stays, so the layout script's observer re-applies the saved layout.
    const grid = document.getElementById('ProductGridContainer');
    grid.innerHTML = doc.getElementById('ProductGridContainer').innerHTML;
    grid.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));

    if (newBar.dataset.pageTitle) document.title = newBar.dataset.pageTitle;
    setHeadTag('meta[name="description"]', 'content', newBar.dataset.pageDescription, () => {
      const meta = document.createElement('meta');
      meta.name = 'description';
      return meta;
    });
    setHeadTag('link[rel="canonical"]', 'href', newBar.dataset.canonical, () => {
      const link = document.createElement('link');
      link.rel = 'canonical';
      return link;
    });

    if (hadFocus) {
      const active = bar.querySelector('.brcr-ctoolbar__cat.is-active');
      if (active) active.focus({ preventScroll: true });
    }
    announce(newBar.dataset.collectionTitle || document.title);

    const top = bar.getBoundingClientRect().top;
    if (top < 0) {
      const header = document.querySelector('sticky-header, .shopify-section-header-sticky, .section-header');
      const offset = header && getComputedStyle(header).position.match(/sticky|fixed/) ? header.offsetHeight : 0;
      window.scrollTo({ top: window.scrollY + top - offset - 12, behavior: 'smooth' });
    }
  }

  async function navigate(href, push) {
    const token = ++navToken;
    const container = document.querySelector('#ProductGridContainer .collection');
    if (container) container.classList.add('loading');
    setActive(href);

    try {
      const doc = await fetchSection(href);
      if (token !== navToken) return;
      const url = new URL(href, window.location.origin);
      const searchParams = url.search.slice(1);
      if (push) history.pushState({ searchParams, brcrCollection: true }, '', url.pathname + url.search);
      currentPath = url.pathname;
      if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = searchParams;
      swap(doc);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'brcr_collection_view',
        page_path: url.pathname + url.search,
        page_title: document.title,
      });
    } catch (error) {
      if (token === navToken) window.location.assign(href);
    }
  }

  function categoryLink(target) {
    const link = target instanceof Element && target.closest('[data-brcr-toolbar] .brcr-ctoolbar__cat');
    if (!link || link.hasAttribute('data-brcr-page-load')) return null;
    const url = new URL(link.href, window.location.origin);
    return url.origin === window.location.origin ? link : null;
  }

  document.addEventListener('click', (event) => {
    const link = categoryLink(event.target);
    if (!link || event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;
    event.preventDefault();
    const url = new URL(link.href);
    if (url.pathname === window.location.pathname && url.search === window.location.search) return;
    navigate(link.href, true);
  });

  const prefetch = (event) => {
    const link = categoryLink(event.target);
    if (link && !link.classList.contains('is-active')) fetchSection(link.href).catch(() => {});
  };
  document.addEventListener('pointerover', prefetch, { passive: true });
  document.addEventListener('touchstart', prefetch, { passive: true });
  document.addEventListener('focusin', prefetch);

  // Capture phase runs before facets.js's popstate listener on window.
  window.addEventListener(
    'popstate',
    (event) => {
      if (window.location.pathname === currentPath) return;
      event.stopImmediatePropagation();
      navigate(window.location.href, false);
    },
    true
  );
})();
